import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Calculator, SpellCheck2, Ruler, BookMarked, BookmarkPlus, Trash2, Lock } from "lucide-react";
import { api, BRL, reasonRequired } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import MoneyInput from "@/components/MoneyInput";
import CalculatorDialog from "@/components/CalculatorDialog";
import MaterialEstimateDialog from "@/components/MaterialEstimateDialog";
import VoiceItemButton from "@/components/VoiceItemButton";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import DeleteItemDialog from "@/components/DeleteItemDialog";
import ServiceCatalogDialog from "@/components/ServiceCatalogDialog";

export const EMPTY_ITEM = { description: "", quantity: 1, unit_value: 0, discount: 0, observation: "", admin_note: "", without_value: false, before_photo_path: null, after_photo_path: null };
const CHANGED_FIELDS = ["description", "quantity", "unit_value", "discount", "observation", "without_value"];
const NO_CATALOG = { open: false, description: "", min_value: 0, max_value: 0, note: "" };

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

const ItemPhotoField = ({ label, path, onUpload, onRemove, testid }) => {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let obj;
    if (path) {
      api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { obj = URL.createObjectURL(r.data); setPreview(obj); }).catch(() => {});
    } else {
      setPreview(null);
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="aspect-video rounded-lg border border-slate-200 bg-slate-50 overflow-hidden relative">
        {preview ? (
          <>
            <img src={preview} alt={label} className="w-full h-full object-cover" />
            <button type="button" onClick={onRemove} data-testid={`${testid}-remove`} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">×</button>
          </>
        ) : (
          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer text-xs text-slate-500 hover:bg-slate-100" data-testid={`${testid}-upload`}>
            + Enviar
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
          </label>
        )}
      </div>
    </div>
  );
};

export function NotifyToggle({ checked, onChange, isEdit }) {
  return (
    <label className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50/60 p-2.5 cursor-pointer" data-testid="item-notify-toggle">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" data-testid="check-item-notify-school" />
      <span className="text-xs text-slate-700">
        <b>Avisar a escola</b>{isEdit ? " e mostrar esta alteração no histórico do diretor" : " sobre o novo item"}
        {!checked && <span className="block text-[11px] text-slate-500 mt-0.5">{isEdit ? "A escola não recebe aviso e a alteração fica só no seu histórico." : "O item aparece no orçamento, mas sem aviso."}</span>}
      </span>
    </label>
  );
}

export function AdminNoteField({ value, onChange, testid = "input-item-admin-note", label = "Observação interna — só você vê" }) {
  return (
    <div className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-amber-700 flex items-center gap-1"><Lock className="w-3 h-3" /> {label}</Label>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="bg-white" placeholder="Não aparece para a escola nem no PDF" data-testid={testid} />
    </div>
  );
}

export function DiscountField({ item, onChange }) {
  const gross = (Number(item.quantity) || 1) * (Number(item.unit_value) || 0);
  const disc = Number(item.discount) || 0;
  return (
    <div className="grid grid-cols-2 gap-3 items-end">
      <Field label="Desconto (R$)">
        <MoneyInput value={item.discount || 0} onChange={onChange} data-testid="input-item-discount" />
      </Field>
      <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs" data-testid="item-total-preview">
        <span className="text-slate-500">Total do item</span>
        <p className={`font-black tabular-nums ${disc > gross ? "text-rose-600" : "text-slate-900"}`}>{BRL(Math.max(0, gross - disc))}</p>
      </div>
    </div>
  );
}

function SaveToCatalogDialog({ state, setState, onConfirm, saving }) {
  const set = (k) => (v) => setState((d) => ({ ...d, [k]: v }));
  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && setState(NO_CATALOG)}>
      <DialogContent className="max-w-md" data-testid="save-to-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookmarkPlus className="w-5 h-5 text-indigo-600" /> Salvar no catálogo</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">Defina uma faixa de valor para este serviço. A escola verá o mínimo, máximo e média quando pesquisar.</p>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Descrição *</Label>
            <Textarea value={state.description} onChange={(e) => set("description")(e.target.value)} rows={2} data-testid="input-cat-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-emerald-700">Valor mínimo (R$) *</Label>
              <MoneyInput value={state.min_value} onChange={set("min_value")} data-testid="input-cat-min" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-rose-700">Valor máximo (R$) *</Label>
              <MoneyInput value={state.max_value} onChange={set("max_value")} data-testid="input-cat-max" />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 -mt-2">Dica: use a mesma unidade (ex.: por lâmpada R$ 15 a R$ 85, ou por metro quadrado de pintura R$ 25 a R$ 60).</p>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Observação (opcional)</Label>
            <Textarea value={state.note} onChange={(e) => set("note")(e.target.value)} rows={2} placeholder="Ex.: valor por unidade, inclui mão de obra, altura até 3m..." data-testid="input-cat-note" />
            <p className="text-[10px] text-slate-500">A escola vê essa observação ao consultar o preço.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState(NO_CATALOG)}>Cancelar</Button>
          <Button onClick={onConfirm} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="btn-confirm-save-catalog">{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Full item editor (add/edit) shared by the budget form and the budget detail screen.
 * With `budget` (saved budget) it persists via the API and calls onSaved(updatedBudget);
 * without it, calls onLocalSave(item) for pending items of a budget not yet created.
 */
export default function BudgetItemDialog({ open, mode, initialItem, budget, schoolId, schoolName, onClose, onSaved, onLocalSave, onDeleted, allowDelete = false }) {
  const [item, setItem] = useState(EMPTY_ITEM);
  const [aiLoading, setAiLoading] = useState(false);
  const [spellLoading, setSpellLoading] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [catalogDlg, setCatalogDlg] = useState(NO_CATALOG);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifySchool, setNotifySchool] = useState(true);
  const savingRef = useRef(false);
  const budgetId = budget?.id || null;
  const needReason = reasonRequired(budget?.status);

  useEffect(() => {
    if (open) { setItem({ ...EMPTY_ITEM, ...(initialItem || {}) }); setNotifySchool(true); }
  }, [open, initialItem]);

  const patch = (p) => setItem((it) => ({ ...it, ...p }));

  const aiCall = async (url, setBusy, okMsg, errMsg) => {
    const prompt = item.description?.trim();
    if (!prompt) { toast.error(url.includes("spell") ? "Digite algo primeiro" : "Digite uma descrição curta para a IA refinar"); return; }
    setBusy(true);
    try {
      const r = await api.post(url, { prompt });
      patch({ description: r.data.text });
      toast.success(okMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || errMsg);
    } finally { setBusy(false); }
  };

  const openSaveToCatalog = () => {
    const desc = item.description?.trim();
    const unit = Number(item.unit_value || 0);
    const total = unit * (Number(item.quantity || 1) || 1);
    if (!desc) { toast.error("Preencha a descrição"); return; }
    if (!unit || unit <= 0) { toast.error("Informe o valor unitário"); return; }
    setCatalogDlg({ open: true, description: desc, min_value: total, max_value: total, note: "" });
  };

  const confirmSaveToCatalog = async () => {
    const { description, min_value, max_value, note } = catalogDlg;
    const mn = Number(min_value || 0);
    const mx = Number(max_value || 0);
    if (!description.trim()) { toast.error("Preencha a descrição"); return; }
    if (!mn || mn <= 0) { toast.error("Informe o valor mínimo"); return; }
    if (!mx || mx <= 0) { toast.error("Informe o valor máximo"); return; }
    setCatalogSaving(true);
    try {
      await api.post("/services-catalog", {
        description: description.trim(), min_value: mn, max_value: mx, note: note || "",
        school_id: schoolId || null, school_name: schoolName || null,
        budget_id: budgetId, budget_number: budget?.number || null,
      });
      toast.success("Salvo no catálogo com a faixa de valor");
      setCatalogDlg(NO_CATALOG);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar no catálogo");
    } finally { setCatalogSaving(false); }
  };

  const pickFromCatalog = (entry) => {
    if (!entry) return;
    const total = entry.min_value != null && entry.max_value != null
      ? (Number(entry.min_value) + Number(entry.max_value)) / 2
      : Number(entry.value || 0);
    setItem((it) => ({ ...it, description: entry.description || it.description, unit_value: Number((total / (Number(it.quantity || 1) || 1)).toFixed(2)) }));
    toast.success("Item preenchido pelo catálogo");
  };

  const uploadPhoto = async (which, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch({ [which]: r.data.path });
      toast.success(`Foto ${which === "before_photo_path" ? "antes" : "depois"} enviada`);
    } catch {
      toast.error("Erro ao enviar foto");
    }
  };

  const save = async () => {
    if (savingRef.current) return;
    if (!item.description?.trim()) { toast.error("Descrição é obrigatória"); return; }
    const payload = {
      description: item.description,
      quantity: Number(item.quantity) || 1,
      unit_value: Number(item.unit_value) || 0,
      discount: item.without_value ? 0 : Math.round((Number(item.discount) || 0) * 100) / 100,
      observation: item.observation || "",
      admin_note: item.admin_note || "",
      without_value: !!item.without_value,
      before_photo_path: item.before_photo_path || null,
      after_photo_path: item.after_photo_path || null,
    };
    if (payload.discount < 0 || payload.discount > payload.quantity * payload.unit_value + 0.001) { toast.error("O desconto não pode ser maior que o valor do item"); return; }
    if (!budgetId) {
      onLocalSave?.({
        ...payload,
        id: item.id || `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        total: payload.quantity * payload.unit_value - payload.discount,
        completed: false,
        paid: false,
      });
      toast.success("Item adicionado");
      return;
    }
    if (mode === "edit") {
      const original = (budget?.items || []).find((p) => p.id === item.id) || {};
      const hasChange = CHANGED_FIELDS.some((k) => {
        const a = payload[k], b = original[k];
        if (typeof a === "number" || typeof b === "number") return Number(a || 0) !== Number(b || 0);
        return (a ?? "") !== (b ?? "");
      });
      if (hasChange && needReason && !(item.edit_reason || "").trim()) { toast.error("Informe o motivo da alteração do item."); return; }
      if (item.edit_reason) payload.edit_reason = item.edit_reason;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const r = mode === "edit"
        ? await api.put(`/budgets/${budgetId}/items/${item.id}`, { ...payload, notify_school: notifySchool })
        : await api.post(`/budgets/${budgetId}/items?notify_school=${notifySchool}`, payload);
      toast.success(mode === "edit" ? `Orçamento Nº ${r.data.number} — item alterado` : `Orçamento Nº ${r.data.number} atualizado — item adicionado`);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar item");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const confirmDelete = async (reason) => {
    try {
      const r = await api.delete(`/budgets/${budgetId}/items/${item.id}?reason=${encodeURIComponent(reason || "")}`);
      toast.success(`Item removido do orçamento Nº ${r.data.number}`);
      setDeleting(false);
      onDeleted?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir item");
    }
  };

  const isEdit = mode === "edit";
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className="sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1.5rem)] sm:w-full grid-cols-1 [&>*]:min-w-0"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (mode !== "edit") setTimeout(() => document.getElementById("budget-item-description")?.focus(), 50);
          }}
          data-testid="item-dialog"
        >
          <DialogHeader>
            <DialogTitle>{isEdit ? "Editar item" : "Adicionar item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Descrição *">
              <div className="flex gap-2 items-start">
                <AutoGrowTextarea
                  value={item.description}
                  onChange={(e) => patch({ description: e.target.value })}
                  minRows={1}
                  maxRows={10}
                  placeholder="Ex.: Pintura das salas de aula, troca de 6 lâmpadas LED..."
                  data-testid="input-item-description"
                  id="budget-item-description"
                />
                <Button type="button" variant="outline" onClick={() => aiCall("/ai/generate-item", setAiLoading, "Descrição gerada pela IA", "Falha ao gerar com IA")} disabled={aiLoading || spellLoading}
                  className="shrink-0 border-[#7c3aed] text-[#7c3aed] hover:bg-[#7c3aed] hover:text-white" data-testid="btn-ai-generate" title="Melhorar descrição com IA">
                  <Sparkles className="w-4 h-4" />
                  {aiLoading ? "..." : "IA"}
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <VoiceItemButton
                  disabled={aiLoading || spellLoading}
                  onParsed={(data) => setItem((it) => ({
                    ...it,
                    description: data.description || it.description,
                    quantity: data.quantity != null ? data.quantity : it.quantity,
                    unit_value: data.unit_value != null ? data.unit_value : it.unit_value,
                  }))}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => aiCall("/ai/spell-correct", setSpellLoading, "Português corrigido", "Falha ao corrigir")} disabled={spellLoading || aiLoading}
                  className="h-8 text-[11px] border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="btn-spell-correct">
                  <SpellCheck2 className="w-3 h-3 mr-1" /> {spellLoading ? "Corrigindo..." : "Corrigir português"}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setEstimateOpen(true)} className="h-8 text-[11px] border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="btn-ai-estimate">
                  <Ruler className="w-3 h-3 mr-1" /> Materiais & valores (IA)
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setCalcOpen(true)} className="h-8 text-[11px] border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="btn-open-calculator">
                  <Calculator className="w-3 h-3 mr-1" /> Calculadora
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setCatalogOpen(true)} className="h-8 text-[11px] border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="btn-open-catalog">
                  <BookMarked className="w-3 h-3 mr-1" /> Catálogo
                </Button>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantidade">
                <Input type="number" min="0" step="0.01" value={item.quantity || ""} placeholder="1" onChange={(e) => patch({ quantity: e.target.value })} data-testid="input-item-qty" />
              </Field>
              <Field label="Valor unitário (R$)">
                <MoneyInput value={item.unit_value} disabled={item.without_value} onChange={(v) => patch({ unit_value: v })} data-testid="input-item-unit" />
              </Field>
            </div>
            <Field label="Observação">
              <Textarea value={item.observation || ""} onChange={(e) => patch({ observation: e.target.value })} rows={2} data-testid="input-item-obs" />
            </Field>
            {!item.without_value && <DiscountField item={item} onChange={(v) => patch({ discount: v })} />}
            <AdminNoteField value={item.admin_note} onChange={(v) => patch({ admin_note: v })} />
            <div className="flex items-center gap-2">
              <Checkbox checked={item.without_value} onCheckedChange={(v) => patch({ without_value: !!v })} data-testid="check-item-no-value" />
              <span className="text-sm text-slate-700">Item sem valor</span>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
              <ItemPhotoField label="Foto ANTES" path={item.before_photo_path} onUpload={(f) => uploadPhoto("before_photo_path", f)} onRemove={() => patch({ before_photo_path: null })} testid="item-photo-before" />
              <ItemPhotoField label="Foto DEPOIS" path={item.after_photo_path} onUpload={(f) => uploadPhoto("after_photo_path", f)} onRemove={() => patch({ after_photo_path: null })} testid="item-photo-after" />
            </div>
            {isEdit && budgetId && (
              <Field label={needReason ? "Motivo da alteração *" : "Motivo da alteração (opcional antes da aprovação)"}>
                <Textarea value={item.edit_reason || ""} onChange={(e) => patch({ edit_reason: e.target.value })} rows={2}
                  placeholder="Ex.: valor ajustado após nova medição..." data-testid="input-item-edit-reason" />
              </Field>
            )}
            {budgetId && <NotifyToggle checked={notifySchool} onChange={setNotifySchool} isEdit={isEdit} />}
            {isEdit && budgetId && item.versions?.length > 0 && (
              <button type="button" onClick={() => setHistoryOpen(true)} className="text-xs text-[#7c3aed] font-semibold hover:underline" data-testid="btn-view-item-history">
                Ver histórico ({item.versions.length} alteraç{item.versions.length === 1 ? "ão" : "ões"})
              </button>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row sm:flex-wrap">
            <Button type="button" variant="outline" onClick={openSaveToCatalog} disabled={catalogSaving || !item.description?.trim() || !Number(item.unit_value || 0)}
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 sm:mr-auto" data-testid="btn-save-to-catalog">
              <BookmarkPlus className="w-4 h-4 mr-1.5" /> Salvar no catálogo
            </Button>
            {isEdit && allowDelete && budgetId && (
              <Button type="button" variant="outline" onClick={() => setDeleting(true)} className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="btn-delete-item-modal">
                <Trash2 className="w-4 h-4 mr-1.5" /> Excluir
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="btn-save-item">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CalculatorDialog open={calcOpen} onOpenChange={setCalcOpen} onUseAsValue={(v) => patch({ unit_value: v })} />
      <MaterialEstimateDialog
        open={estimateOpen}
        onOpenChange={setEstimateOpen}
        onUseDescription={(txt) => patch({ description: txt })}
        onUseObservation={(txt) => setItem((it) => {
          const base = (it.observation || "").trim();
          return { ...it, observation: base ? `${base}\n\n${txt}` : txt };
        })}
      />
      <ServiceCatalogDialog open={catalogOpen} onOpenChange={setCatalogOpen} initialQuery={item.description || ""} onPick={pickFromCatalog} />
      <SaveToCatalogDialog state={catalogDlg} setState={setCatalogDlg} onConfirm={confirmSaveToCatalog} saving={catalogSaving} />
      {budgetId && <ItemHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} budgetId={budgetId} itemId={item.id} />}
      <DeleteItemDialog open={deleting} onOpenChange={(o) => { if (!o) setDeleting(false); }} item={item} onConfirm={confirmDelete} reasonRequired={needReason} />
    </>
  );
}
